import { logger } from '@adornis/base/logging.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-link.js';
import { type Renderable } from '@adornis/chemistry/renderable.js';
import type { DataViewOptions } from '@adornis/data-view/client/data-view-options.js';
import { type ColumnDefinition } from '@adornis/data-view/db/view.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import { MailEventLog, MailEventWithUser } from '@chancenwerk/db/eventlogs/MailEventlog.js';
import '@chancenwerk/design/components/cr-campus-loader.js';
import '@chancenwerk/design/components/cr-card.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import '@chancenwerk/design/components/cr-tabs.js';
import '@chancenwerk/design/components/fonts.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { choose } from 'lit/directives/choose.js';
import { ChancenDialogMailProgress } from '../../../../src/client/components/chancen-dialog-mail-progress.js';

type possibleStats = 'sentMails' | 'notSentMails' | 'failedMails';

/**
 * @customElement campus-page-administration-children-statistics
 */
@customElement('campus-page-administration-children-statistics')
export class CampusPageAdministrationChildrenStatistics extends ChemistryLitElement {
  @state() size = new WindowSizeController(this);

  @state() private _showStats?: possibleStats;

  @state() private readonly _sentRegistrationMailsCount = new RXController(
    this,
    MailEventLog.getSentRegistrationMailsCount()(),
  );
  @state() private readonly _noRegistrationMailCount = new RXController(
    this,
    MailEventLog.getNoRegistrationMailsCount()(),
  );
  @state() private readonly _failedRegistrationMailCount = new RXController(
    this,
    MailEventLog.getFailedRegistrationMailsCount()(),
  );

  private readonly _sharedColumnDefinitions: ColumnDefinition<typeof MailEventLog>[] = [
    {
      columnName: 'Vorname',
      customID: 'vorname',
      getFieldMapping(entityInstance) {
        return entityInstance.vorname ?? '-';
      },
    },
    {
      columnName: 'Nachname',
      customID: 'nachname',
      getFieldMapping(entityInstance) {
        return entityInstance.nachname ?? '-';
      },
    },
    {
      columnName: 'E-Mail',
      customID: 'email',
      getFieldMapping(entityInstance) {
        return entityInstance.email ?? '-';
      },
    },
  ];

  private readonly _columnDefinitionsSentMails = [
    ...this._sharedColumnDefinitions,
    {
      columnName: 'Datum Anmeldungs-Mail',
      customID: 'info',
      getFieldMapping(entityInstance) {
        return entityInstance.info;
      },
    },
  ] as Array<ColumnDefinition<MailEventWithUser>>;

  private readonly _columnDefinitionsNotSentMails: ColumnDefinition<typeof MailEventLog>[] = [
    ...this._sharedColumnDefinitions,
    {
      columnName: '',
      columnWidth: 'max-content',
      customID: 'forward',
      renderItem: entityInstance =>
        html`
          <x-flex center>
            <cr-button
              icon="forward_to_inbox"
              @click=${() =>
                ChromiumDialog.waitFor(
                  ChancenwerkUser.sendRegistrationMailForUserID(entityInstance.userID)()
                    .then(() => ChromiumSnackbar.show('Mail erfolgreich versandt'))
                    .catch(err => {
                      logger.error({ err }, 'Es gab ein Problem beim Versenden der Mail');
                      return ChromiumDialog.alert('Es gab ein Problem beim Versenden der Mail');
                    }),
                )}
            >
              Mail senden
            </cr-button>
          </x-flex>
        `,
    },
  ] as Array<ColumnDefinition<MailEventWithUser>>;

  private readonly _columnDefinitionsFailedMails: ColumnDefinition<typeof MailEventLog>[] = [
    ...this._sharedColumnDefinitions,
    {
      columnName: 'Fehlermeldung',
      customID: 'info',
      columnWidth: '3fr',
      getFieldMapping(entityInstance) {
        return entityInstance.info;
      },
      renderItem: entityInstance =>
        html`
          <x-flex padding="sm" ${css({ padding: `${this.spacing.xs} 0` })}>
            <cr-text> ${entityInstance.info} </cr-text>
          </x-flex>
        `,
    },
    {
      columnName: '',
      columnWidth: 'max-content',
      customID: 'send_mail',
      renderItem: entityInstance =>
        html` <x-flex center> <cr-button icon="forward_to_inbox"> Mail senden </cr-button> </x-flex> `,
    },
  ];

  override render() {
    return html`
      <cr-flex padding="${this.size.mobile ? 'md' : 'lg'}" space="lg">
        <cr-flex space="lg">
          <cr-h2> CHANCENcampus Zugänge </cr-h2>
          <cr-text> Hier kannst du zu alle Campus-Zugänge und deren Status überwachen. </cr-text>
        </cr-flex>
        ${this._showStats ? this._stats() : this._overview()}
      </cr-flex>
    `;
  }

  private _overview() {
    return html`
      <cr-grid space="md" auto-fit-columns="300px">
        ${this._card(
          'Zugesendete Mails für die Anmeldung',
          html`
            <cr-h1 ${css({ color: this.colors.accent, marginBottom: this.spacing.md })}>
              ${this._sentRegistrationMailsCount.isLoading
                ? html`<cr-loader ${css({ width: '30px', height: '30px', marginTop: this.spacing.sm })}></cr-loader>`
                : this._sentRegistrationMailsCount.value}
            </cr-h1>
            Kinder und Übungsleiter haben eine Anmeldungs-Mail zugesendet bekommen
          `,
          'sentMails',
        )}
        ${this._card(
          'Ohne zugesendete Anmeldungsmail',
          html`
            <cr-h1 ${css({ color: this.colors.accent, marginBottom: this.spacing.md })}>
              ${this._noRegistrationMailCount.isLoading
                ? html`<cr-loader ${css({ width: '30px', height: '30px', marginTop: this.spacing.sm })}></cr-loader>`
                : this._noRegistrationMailCount.value}
            </cr-h1>
            Kinder und Übungsleiter haben noch keine Anmeldungs-Mail zugesendet bekommen
          `,
          'notSentMails',
        )}
        ${this._card(
          'Fehlerhafter Mailversand',
          html`
            <cr-h1 ${css({ color: this.colors.accent, marginBottom: this.spacing.md })}>
              ${this._failedRegistrationMailCount.isLoading
                ? html`<cr-loader ${css({ width: '30px', height: '30px', marginTop: this.spacing.sm })}></cr-loader>`
                : this._failedRegistrationMailCount.value}
            </cr-h1>
            Mails an Kinder und Übungsleiter konnten nicht zugestellt werden
          `,
          'failedMails',
        )}
      </cr-grid>
    `;
  }

  private _stats() {
    if (!this._showStats) return nothing;
    return html`
      <cr-tabs .selected=${['sentMails', 'notSentMails', 'failedMails'].indexOf(this._showStats)}>
        <cr-tab @click=${() => (this._showStats = 'sentMails')}> Zugesendete Mails für die Anmeldungen </cr-tab>
        <cr-tab @click=${() => (this._showStats = 'notSentMails')}> Ohne zugesendete Anmeldungs-Mail </cr-tab>
        <cr-tab @click=${() => (this._showStats = 'failedMails')}> Fehlerhafte Mails </cr-tab>
      </cr-tabs>
      ${this._detailStats()}
      ${choose(
        this._showStats,
        [
          [
            'failedMails',
            () => html`
              <x-data-view
                .options=${{
                  class: MailEventLog,
                  operation: MailEventLog.subscribeFailedRegistrationMailsStudents,
                  selectionSet: MailEventWithUser.allFields,
                  onlyShowLastTranslation: true,
                  customColumns: this._columnDefinitionsFailedMails,
                } as DataViewOptions<typeof MailEventLog>}
              ></x-data-view>
            `,
          ],
          [
            'sentMails',
            () => html`
              <x-data-view
                .options=${{
                  class: MailEventLog,
                  operation: MailEventLog.subscribeSentRegistrationMailsStudents,
                  selectionSet: MailEventWithUser.allFields,
                  onlyShowLastTranslation: true,
                  customColumns: this._columnDefinitionsSentMails,
                } as DataViewOptions<typeof MailEventLog>}
              ></x-data-view>
            `,
          ],
          [
            'notSentMails',
            () => html`
              <x-flex space="md">
                <x-flex horizontal end>
                  <cr-button
                    mode="outline"
                    icon="forward_to_inbox"
                    @click=${() =>
                      ChancenDialogMailProgress.showPopup({
                        modal: true,
                        props: {
                          bqlSubscription:
                            ChancenwerkUser.resendRegistrationMailsToStudentsWithoutSentRegistrationMail(),
                        },
                      })}
                  >
                    Mail an alle erneut versenden
                  </cr-button>
                </x-flex>
                <x-data-view
                  .options=${{
                    class: MailEventLog,
                    operation: MailEventLog.subscribeNoRegistrationMailsStudents,
                    selectionSet: MailEventWithUser.allFields,
                    onlyShowLastTranslation: true,
                    customColumns: this._columnDefinitionsNotSentMails,
                  } as DataViewOptions<typeof MailEventLog>}
                ></x-data-view>
              </x-flex>
            `,
          ],
        ],
        () => html``,
      )}
    `;
  }

  private _detailStats() {
    switch (this._showStats) {
      case 'sentMails':
        return html`
          <cr-flex horizontal wrap space="md">
            ${this._card(
              undefined,
              html`
                Insgesamt haben
                <span ${css({ color: this.colors.accent })}>
                  ${this._sentRegistrationMailsCount.isLoading ? '...' : this._sentRegistrationMailsCount.value}
                </span>
                Kinder und Übungsleiter eine Anmeldungs-Mail zugesendet bekommen.
              `,
            )}
          </cr-flex>
        `;
      case 'notSentMails':
        return html`
          <cr-flex horizontal wrap space="md">
            ${this._card(
              undefined,
              html`
                Insgesamt haben
                <span ${css({ color: this.colors.accent })}>
                  ${this._noRegistrationMailCount.isLoading ? '...' : this._noRegistrationMailCount.value}
                </span>
                Kinder und Übungsleiter noch <span ${css({ color: this.colors.accent })}> keine </span> Anmeldungs-Mail
                erhalten.
              `,
            )}
          </cr-flex>
        `;
      case 'failedMails':
        return html`
          <cr-flex horizontal wrap space="md">
            ${this._card(
              undefined,
              html`
                <span ${css({ color: this.colors.accent })}> Fehlgeschlagen </span> ist der Mailversand bei
                <span ${css({ color: this.colors.accent })}>
                  ${this._failedRegistrationMailCount.isLoading ? '...' : this._failedRegistrationMailCount.value}
                </span>
                Kindern und Übungsleitern
              `,
            )}
          </cr-flex>
        `;
      default:
        return nothing;
    }
  }

  private _card(title?: Renderable, subtitle?: Renderable, stats?: possibleStats) {
    const card = html`
      <cr-card padding="md" href="#">
        <cr-flex space="md">
          ${title ? html` <chancen-component-card-title> ${title} </chancen-component-card-title> ` : nothing}
          ${subtitle ? html` <cr-text> ${subtitle} </cr-text> ` : nothing}
        </cr-flex>
      </cr-card>
    `;
    if (stats) return html` <x-link @click=${() => (this._showStats = stats)}> ${card} </x-link> `;
    return card;
  }
}
